/* Teng Tools USA — True Size PDP panel (2026-07-18).
   Renders a calibratable, actual-size drawing of the product silhouette on
   the PDP, using a bank card (85.6mm wide, ISO/IEC 7810 ID-1) as the
   real-world calibration reference — the classic "hold a card to the
   screen" size-checker trick, made self-calibrating per device/zoom.

   Data comes from window.__ttTrueSize (see snippets/tt-true-size.liquid),
   itself sourced from the product's custom.silhouette metafield, written by
   backend/src/services/teng-is/trueSizePublisher.service.js:
     { d, w, h, source, length_mm, family }

   Silhouette render convention (silhouetteEngine.service.js):
     source==='traced'     -> svg_path is POTRACE native space; MUST wrap in
                               <g transform="translate(0,h) scale(0.1,-0.1)">.
     source==='parametric' -> clean top-left coordinates; NO wrap.

   All markup rendered here is built from OUR OWN trusted backend data (the
   Gemini catalogue parse + our own generated/traced SVG paths) — never from
   shopper input — so string-built innerHTML is the same trusted-data pattern
   already used by this theme's cf-app.js configurator. Every family label is
   still HTML-escaped defensively (escapeHtml) in case a title ever contains
   a stray "<" or "&".

   This script must NEVER throw or block PDP render. Every DOM lookup is
   defensive; the whole body runs inside one try/catch; a bail-out (missing
   data, no anchor point, unreliable length) is a silent no-op, not an error.
   Vanilla ES5 — no build step, no dependencies (ships to Shopify as a plain
   theme asset, same as cf-app.js). */
(function () {
  'use strict';

  var CARD_MM_W = 85.6; // ISO/IEC 7810 ID-1 card width
  var CARD_MM_H = 53.98; // ID-1 card height
  var STORAGE_KEY = 'tt_pxmm';
  var DEFAULT_CARD_PX = 324;

  function findAnchor() {
    // 1) Under the product gallery, in the WIDE media column. True Size is about
    //    physical size, so it needs real width — the right-hand info column is
    //    ~416px and forces a 310mm tool to scroll, which defeats the point. The
    //    media column is ~880px and has empty space below the gallery.
    var gal = document.querySelector('media-gallery, [class*="media-gallery"]');
    if (gal && gal.parentNode) return gal;
    // 2) The spec table block — correct but narrow; used when there's no gallery.
    var el = document.querySelector('.tt-spec');
    if (el) return el;
    // 2) An explicit .ttdim container, if the markup exposes one directly.
    el = document.querySelector('.ttdim');
    if (el) return el;
    // 2) Scan likely description containers for the ttdim marker comment —
    //    Shopify preserves HTML comments through descriptionHtml, so the
    //    comment text is present in innerHTML even though it renders nothing.
    var nodes = document.querySelectorAll('[class*="description"], .rte, .product__description, main, article');
    var j;
    for (j = 0; j < nodes.length; j++) {
      if (nodes[j].innerHTML && nodes[j].innerHTML.indexOf('ttdim:start') !== -1) {
        return nodes[j];
      }
    }
    // 3) Fall back to any generic description container, even without the
    //    marker — still a sane place to append the panel.
    el = document.querySelector('[class*="description"]');
    if (el) return el;
    return null;
  }

  function readPxmm() {
    var stored;
    try {
      stored = window.localStorage.getItem(STORAGE_KEY);
    } catch (_e) {
      stored = null;
    }
    var n = parseFloat(stored);
    if (isFinite(n) && n > 0) return n;
    return DEFAULT_CARD_PX / CARD_MM_W;
  }

  function writePxmm(pxmm) {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(pxmm));
    } catch (_e) {
      /* localStorage unavailable (private mode / disabled) — calibration
         just won't persist across page loads; never block rendering. */
    }
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function noteText(source) {
    return source === 'traced'
      ? 'Shown at actual size. Hold a ruler to the scale to check.'
      : 'Drawn from catalogue dimensions at actual size.';
  }

  function buildPanel(data) {
    var div = document.createElement('div');
    div.className = 'tt-true-size';
    div.style.cssText =
      'max-width:100%;width:100%;min-width:0;flex:none;box-sizing:border-box;margin:16px 0;'+
      'font:inherit;color:inherit;opacity:0;transition:opacity .28s ease;';
    div.innerHTML =
      '<button type="button" class="tt-ts-toggle" aria-expanded="true" '+
      'style="display:inline-flex;align-items:center;gap:6px;background:none;border:0;padding:0;'+
      'font:inherit;font-size:14px;font-weight:600;color:inherit;cursor:pointer;">'+
      '<span class="tt-ts-caret" style="display:inline-block;transition:transform .15s;transform:rotate(90deg);">&#9656;</span>'+
      'See it at actual size</button>' +
      '<div class="tt-ts-body" style="display:block;margin-top:12px;">' +
      '<div style="display:flex;align-items:center;gap:12px;margin-bottom:8px;">' +
      '<span style="font-size:12px;opacity:0.7;flex-shrink:0;">Calibrate to a bank card</span>' +
      '<input type="range" class="tt-ts-range" min="200" max="520" value="' +
      DEFAULT_CARD_PX +
      '" style="flex:1;min-width:120px;max-width:280px;">' +
      '</div>' +
      '<div class="tt-ts-card" style="border:2px solid currentColor;border-radius:6px;opacity:0.5;margin-bottom:6px;"></div>' +
      '<div style="font-size:11px;opacity:0.6;margin-bottom:16px;">Match any bank card (85.6 mm wide)</div>' +
      '<div class="tt-ts-svg-wrap" style="margin-bottom:8px;"></div>' +
      '<div class="tt-ts-ruler-wrap" style="margin-bottom:4px;min-width:0;width:100%;overflow-x:auto;"></div>' +
      '<div class="tt-ts-family-wrap"></div>' +
      '<div style="font-size:11px;opacity:0.6;margin-top:8px;">' +
      escapeHtml(noteText(data.source)) +
      '</div></div>';
    return div;
  }

  try {
    var data = window.__ttTrueSize;
    if (!data || typeof data !== 'object') return;
    if (!data.d || !isFinite(data.w) || !isFinite(data.h) || data.w <= 0 || data.h <= 0) return;

    var family = [];
    var i;
    if (Array.isArray(data.family)) {
      for (i = 0; i < data.family.length; i++) {
        var f = data.family[i];
        if (f && isFinite(f.length_mm) && f.length_mm > 0) family.push(f);
      }
    }

    // Never invent a physical length from viewbox pixel dimensions — only
    // ever use a real length_mm sourced from the silhouette row or a family
    // member. No reliable length anywhere means no True Size panel.
    var initialLength = null;
    if (isFinite(data.length_mm) && data.length_mm > 0) {
      initialLength = Number(data.length_mm);
    } else if (family.length) {
      initialLength = Number(family[0].length_mm);
    }
    if (!initialLength) return;

    var anchor = findAnchor();
    if (!anchor || !anchor.parentNode) return;

    var state = { lengthMm: initialLength, pxmm: readPxmm() };

    var panel = buildPanel(data);

    // The gallery sits INSIDE the theme's sticky column
    // (.sticky-content--desktop { position: sticky; z-index: var(--layer-flat) }).
    // Inserting the panel as a sibling of the gallery therefore trapped it in
    // that sticky box, so the pinned image painted straight over the panel on
    // scroll and hid it. Hoist the panel out to just after the sticky host, so
    // it sits in normal flow below the image column and scrolls with the page.
    var stickyHost = anchor.closest
      ? anchor.closest('.sticky-content, .sticky-content--desktop')
      : null;
    var mount = stickyHost && stickyHost.parentNode ? stickyHost : anchor;
    mount.parentNode.insertBefore(panel, mount.nextSibling);

    // Belt and braces: even in normal flow, give the panel its own stacking
    // context so no later sticky/absolute sibling can paint over it.
    panel.style.position = 'relative';
    panel.style.zIndex = '1';

    // Column width measured while collapsed. Every wide child is hard-pinned
    // to this so its intrinsic width can never propagate up the flex/grid
    // chain and squeeze the product gallery — it scrolls inside instead.
    state.availW = Math.max(
      220,
      Math.round(panel.clientWidth || (panel.parentNode && panel.parentNode.clientWidth) || 320)
    );

    var toggleEl = panel.querySelector('.tt-ts-toggle');
    var bodyEl = panel.querySelector('.tt-ts-body');
    var caretEl = panel.querySelector('.tt-ts-caret');
    if (toggleEl && bodyEl) {
      toggleEl.addEventListener('click', function () {
        var open = bodyEl.style.display !== 'none';
        bodyEl.style.display = open ? 'none' : 'block';
        toggleEl.setAttribute('aria-expanded', open ? 'false' : 'true');
        if (caretEl) caretEl.style.transform = open ? 'rotate(0deg)' : 'rotate(90deg)';
        if (!open) render();
      });
    }

    var rangeEl = panel.querySelector('.tt-ts-range');
    var cardEl = panel.querySelector('.tt-ts-card');
    var svgWrap = panel.querySelector('.tt-ts-svg-wrap');
    var rulerWrap = panel.querySelector('.tt-ts-ruler-wrap');
    var familyWrap = panel.querySelector('.tt-ts-family-wrap');

    if (rangeEl) {
      rangeEl.value = String(Math.round(state.pxmm * CARD_MM_W));
      rangeEl.addEventListener('input', function () {
        var px = parseFloat(rangeEl.value);
        if (!isFinite(px) || px <= 0) return;
        state.pxmm = px / CARD_MM_W;
        writePxmm(state.pxmm);
        render();
      });
    }

    render();

    // Reveal only after the first render so the panel eases in rather than
    // snapping open mid-paint (the 'glitchy' jump on page load).
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () { panel.style.opacity = '1'; });
      });
    } else {
      panel.style.opacity = '1';
    }

    function pinWrap(el) {
      if (!el) return;
      el.style.minWidth = '0';
      el.style.width = state.availW + 'px';
      el.style.maxWidth = '100%';
      el.style.overflowX = 'auto';
    }

    function render() {
      renderCard();
      renderSilhouette();
      renderRuler();
      renderFamily();
    }

    function renderCard() {
      if (!cardEl) return;
      cardEl.style.width = state.pxmm * CARD_MM_W + 'px';
      cardEl.style.height = state.pxmm * CARD_MM_H + 'px';
    }

    function renderSilhouette() {
      if (!svgWrap) return;
      var w = Number(data.w);
      var h = Number(data.h);
      var targetW = state.lengthMm * state.pxmm;
      var scale = w > 0 ? targetW / w : 1;
      var targetH = h * scale;
      var inner;
      if (data.source === 'traced') {
        inner =
          '<g transform="translate(0,' + h + ') scale(0.1,-0.1)"><path d="' + data.d + '" fill="currentColor"/></g>';
      } else {
        inner = '<path d="' + data.d + '" fill="currentColor"/>';
      }
      // Themes commonly ship `svg { max-width: 100% }`, which silently squashes
      // the drawing to the column width — the ruler would then say one thing and
      // the silhouette another, which is worse than no panel at all. Pin the
      // exact physical width inline and defeat any max-width rule; the wrapper
      // scrolls instead (that IS the answer for tools longer than the screen).
      // NEVER set a min-width here: it propagates up the flex/grid chain and
      // widens the whole column (it collapsed the product gallery once).
      // The wrapper stays column-width and scrolls; only the SVG is wide.
      pinWrap(svgWrap);
      var svgStyle =
        'display:block;color:currentColor;width:' + targetW + 'px;height:' + targetH +
        'px;max-width:none;flex:none;';
      svgWrap.innerHTML =
        '<svg width="' + targetW + '" height="' + targetH + '" viewBox="0 0 ' + w + ' ' + h + '" ' +
        'xmlns="http://www.w3.org/2000/svg" style="' + svgStyle + '">' + inner + '</svg>';
    }

    function renderRuler() {
      if (!rulerWrap) return;
      pinWrap(rulerWrap);
      var rulerMm = Math.max(50, Math.ceil(state.lengthMm / 50) * 50);
      var pxmm = state.pxmm;
      var html = '<div style="position:relative;height:26px;width:' + rulerMm * pxmm + 'px;">';
      var mm;
      for (mm = 0; mm <= rulerMm; mm += 10) {
        var isMajor = mm % 50 === 0;
        var left = mm * pxmm;
        html +=
          '<div style="position:absolute;left:' + left + 'px;bottom:0;width:1px;height:' +
          (isMajor ? 14 : 8) +
          'px;background:currentColor;opacity:' +
          (isMajor ? 0.7 : 0.35) +
          ';"></div>';
        if (isMajor) {
          // The first label would be clipped by the container's left edge if
          // centred, so left-align it; centre every other one.
          var shift = mm === 0 ? 'translateX(0)' : 'translateX(-50%)';
          html +=
            '<div style="position:absolute;left:' + left + 'px;bottom:14px;transform:' + shift + ';' +
            'font-size:10px;opacity:0.6;">' + mm / 10 + '</div>';
        }
      }
      html +=
        '<div style="position:absolute;left:' + (rulerMm * pxmm + 4) + 'px;bottom:0;font-size:10px;opacity:0.6;">cm</div>';
      html += '</div>';
      rulerWrap.innerHTML = html;
    }

    function renderFamily() {
      if (!familyWrap) return;
      if (family.length <= 1) {
        familyWrap.innerHTML = '';
        return;
      }
      var maxLen = 0;
      var j;
      for (j = 0; j < family.length; j++) {
        if (family[j].length_mm > maxLen) maxLen = family[j].length_mm;
      }
      if (maxLen <= 0) {
        familyWrap.innerHTML = '';
        return;
      }
      var html = '<div style="margin-top:8px;">';
      for (j = 0; j < family.length; j++) {
        var fam = family[j];
        var pct = Math.max(4, Math.round((fam.length_mm / maxLen) * 100));
        var active = Math.abs(fam.length_mm - state.lengthMm) < 0.01;
        html +=
          '<div class="tt-ts-family-row" data-length="' + fam.length_mm + '" ' +
          'style="display:flex;align-items:center;gap:8px;cursor:pointer;padding:3px 0;' +
          (active ? 'font-weight:600;' : '') +
          '">' +
          '<span style="width:64px;font-size:12px;flex-shrink:0;">' + escapeHtml(fam.size || '') + '</span>' +
          '<span style="flex:1;height:8px;background:currentColor;opacity:' +
          (active ? '0.55' : '0.2') +
          ';border-radius:4px;max-width:' + pct + '%;"></span>' +
          '<span style="width:70px;font-size:11px;opacity:0.65;text-align:right;flex-shrink:0;">' +
          fam.length_mm +
          ' mm</span>' +
          '</div>';
      }
      html += '</div>';
      familyWrap.innerHTML = html;

      var rows = familyWrap.querySelectorAll('.tt-ts-family-row');
      var k;
      for (k = 0; k < rows.length; k++) {
        rows[k].addEventListener('click', function (ev) {
          var lengthMm = parseFloat(ev.currentTarget.getAttribute('data-length'));
          if (!isFinite(lengthMm) || lengthMm <= 0) return;
          state.lengthMm = lengthMm;
          render();
        });
      }
    }
  } catch (_err) {
    /* True Size panel must never break the PDP — silently no-op. */
  }
})();
